import { Link } from "react-router-dom";
import { ArrowRight, Package } from "lucide-react";
import type { Category } from "@aurelia/types";
import { GiftBagVisual } from "./GiftBagVisual";

const FALLBACK_GRADIENTS = [
  "linear-gradient(135deg, rgba(220,182,98,0.18), transparent)",
  "linear-gradient(135deg, rgba(255,255,255,0.06), rgba(220,182,98,0.14))",
  "linear-gradient(160deg, rgba(220,182,98,0.14), rgba(255,255,255,0.03))"
];

function QuickTile({
  to,
  label,
  imageUrl,
  icon: Icon,
  visual,
  index
}: {
  to: string;
  label: string;
  imageUrl?: string | null;
  icon?: typeof Package;
  visual?: React.ReactNode;
  index: number;
}) {
  return (
    <Link to={to} className="group flex flex-col items-center gap-2.5 shrink-0 w-20 sm:w-28 lg:w-32">
      <div
        className="h-20 w-20 sm:h-28 sm:w-28 lg:h-32 lg:w-32 rounded-2xl overflow-hidden border border-white/10 bg-white/[0.04] flex items-center justify-center transition-transform duration-300 ease-physical group-hover:-translate-y-1 group-hover:border-[#dcb662]/40"
        style={!imageUrl && !visual ? { background: FALLBACK_GRADIENTS[index % FALLBACK_GRADIENTS.length] } : undefined}
      >
        {imageUrl ? (
          <img src={imageUrl} alt="" className="h-full w-full object-cover" />
        ) : visual ? (
          <div className="h-[78%] w-[78%]">{visual}</div>
        ) : Icon ? (
          <Icon size={26} className="text-[#dcb662]" aria-hidden />
        ) : null}
      </div>
      <span className="text-xs sm:text-sm font-medium text-center truncate w-full text-ink">{label}</span>
    </Link>
  );
}

// Sits directly below the hero — a fast-scan shortcut row plus a real,
// data-derived deals card, both inside one elevated dark surface. Distinct
// from the larger "Shop by category" section further down the page, which
// stays as the fuller browse grid.
//
// categoryImageFallback lets a category without its own admin-uploaded
// imageUrl borrow a real photo from one of its own products instead of
// falling back straight to a gradient — still real catalog data, never a
// stock/placeholder image.
export function CategoryQuickLinks({
  categories,
  maxDiscountPercent,
  categoryImageFallback,
  footer
}: {
  categories: Category[];
  maxDiscountPercent: number | null;
  categoryImageFallback: Record<string, string>;
  footer?: React.ReactNode;
}) {
  return (
    <section className="max-w-7xl mx-auto px-6 pt-8 sm:pt-10 pb-2">
      <div className="rounded-3xl border border-white/10 bg-white/[0.03] px-5 sm:px-9 py-7 sm:py-10">
        <div className="flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-10">
          <div className="flex-1 min-w-0 flex gap-4 sm:gap-6 overflow-x-auto no-scrollbar pb-1">
            {categories.map((cat, i) => (
              <QuickTile
                key={cat.id}
                to={`/category/${cat.slug}`}
                label={cat.name}
                imageUrl={cat.imageUrl ?? categoryImageFallback[cat.slug]}
                index={i}
              />
            ))}
            <QuickTile to="/bundles" label="Bundles" icon={Package} index={categories.length} />
            <QuickTile
              to="/offers"
              label="Offers"
              visual={<GiftBagVisual className="h-full w-full" />}
              index={categories.length + 1}
            />
          </div>

          {maxDiscountPercent !== null && (
            <>
              <div className="hidden lg:block w-px self-stretch bg-white/10" aria-hidden />
              <div className="block lg:hidden h-px w-full bg-white/10" aria-hidden />
              <Link to="/offers" className="flex items-center gap-5 shrink-0 pl-0 lg:pl-2 pr-3 group">
                <div className="flex flex-col gap-2">
                  <p className="text-xs text-ink-muted uppercase tracking-[0.1em]">Exclusive deals</p>
                  <p className="text-3xl font-serif text-[#dcb662] tracking-tight leading-none">
                    Up to {maxDiscountPercent}% OFF
                  </p>
                  <p className="text-sm text-ink-muted">on selected items</p>
                  <span className="mt-1 inline-flex items-center gap-2 rounded-full bg-[#dcb662] text-[#0b1330] text-sm font-medium pl-4 pr-1.5 py-1.5 w-fit transition-colors duration-300 group-hover:bg-[#edd08a]">
                    Shop now
                    <span className="h-6 w-6 rounded-full bg-[#0b1330]/90 text-[#dcb662] flex items-center justify-center">
                      <ArrowRight size={12} aria-hidden />
                    </span>
                  </span>
                </div>
                <GiftBagVisual className="h-32 w-28 shrink-0 opacity-95 transition-transform duration-300 group-hover:-translate-y-1" />
              </Link>
            </>
          )}
        </div>

        {footer}
      </div>
    </section>
  );
}
